import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

const normalizeTimestamp = (value) => {
  if (!value) return value;
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime())
      ? date.toISOString()
      : value;
  }
  if (typeof value.seconds === "number") {
    const millis =
      value.seconds * 1000 +
      Math.floor(Number(value.nanoseconds || 0) / 1000000);
    const date = new Date(millis);
    return Number.isFinite(date.getTime()) ? date.toISOString() : value;
  }
  return value;
};

const normalizeSubjectExperimentContext = (
  experimentKey = "",
  value = {},
  { subjectScope = "", subjectId = "" } = {},
) => {
  const context = value && typeof value === "object" ? value : {};
  return {
    experimentKey: String(experimentKey || "").trim(),
    subjectScope: context.subjectScope || subjectScope || "",
    subjectId: context.subjectId || subjectId || "",
    ...context,
    assignedAt: normalizeTimestamp(context.assignedAt || context.updatedAt),
    updatedAt: normalizeTimestamp(context.updatedAt),
    processedAt: normalizeTimestamp(context.processedAt),
  };
};

const normalizeSubjectExperiments = (experiments = {}, context = {}) =>
  Object.fromEntries(
    Object.entries(experiments || {}).map(([experimentKey, value]) => [
      String(experimentKey || "").trim(),
      normalizeSubjectExperimentContext(experimentKey, value, context),
    ]),
  );

const fetchScopedExperimentContexts = async ({
  userId = "",
  subjectScope = "user",
} = {}) => {
  try {
    const snap = await getDoc(
      doc(db, "experimentSubjects", subjectScope, "subjects", userId),
    );
    if (!snap.exists()) return {};
    return normalizeSubjectExperiments(snap.data()?.experiments || {}, {
      subjectScope,
      subjectId: userId,
    });
  } catch (error) {
    const code = String(error?.code || "").toLowerCase();
    if (code === "permission-denied" || code === "invalid-argument") {
      return {};
    }
    console.error("Failed to read experiment subject state:", error);
    return {};
  }
};

export const fetchUserExperimentContexts = async ({ userId = "" } = {}) => {
  const resolvedUserId = String(userId || "").trim();
  if (!resolvedUserId) return {};

  const [userContexts, memberContexts] = await Promise.all([
    fetchScopedExperimentContexts({
      userId: resolvedUserId,
      subjectScope: "user",
    }),
    fetchScopedExperimentContexts({
      userId: resolvedUserId,
      subjectScope: "member",
    }),
  ]);
  return {
    ...userContexts,
    ...memberContexts,
  };
};
